import React, { useState, useEffect } from 'react'
import { BsFillPersonFill } from "react-icons/bs";
import { Form, Button, Dropdown, Spinner } from "react-bootstrap"
import { useParams, useNavigate } from 'react-router-dom';
import { GoMail, GoClock, GoGraph, GoAlert, GoPlus, GoStarFill, GoHourglass} from "react-icons/go";
import { RxCross2 } from "react-icons/rx";
import { FaFemale, FaMale, FaRegCalendar, FaStethoscope, FaExclamationTriangle, FaUserMd } from "react-icons/fa";
import axios from 'axios'
import { BASE_URL, STUDY_ABBREVIATION_MAP } from '../../utils/constants';
import { Oval } from "react-loader-spinner"
import { toast } from 'react-toastify';
import CustomTimeline from '../../components/CustomTimeline/CustomTimeline';
import GraphCard from '../../components/GraphCard/GraphCard';
import _ from 'underscore'
import { LineChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';

import validator from 'validator'
import moment from 'moment'

import "./patientDashboard.scss"

// Surgery color palette (supports up to 7 surgeries)
// Avoiding green (surgery) and red (complications)
const SURGERY_COLORS = [
  '#89CFF0',  // Pastel Blue
  '#FFB347',  // Pastel Orange
  '#C4A1D9',  // Pastel Purple
  '#77DD77',  // Pastel Green
  '#F49AC2',  // Pastel Pink
  '#FFD1A3',  // Pastel Peach
  '#F0E68C',  // Pastel Yellow
];

// Custom tooltip component matching Analysis.js
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <div className="tooltip-header">
          <h4 className="tooltip-title">{label}</h4>
        </div>
        <div className="tooltip-content">
          {payload.map((entry, index) => (
            <div key={index} className="tooltip-item">
              <div className="tooltip-item-header">
                <div
                  className="tooltip-color-indicator"
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="tooltip-group-name">{entry.name}</span>
              </div>
              <div className="tooltip-value">
                <span className="value-number">{entry.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const shoulderArthroplastyMilestones = [
  "pre_treatment",
  "two_weeks",
  "six_weeks",
  "three_months",
  "six_months",
  "one_year",
  "two_years",
  "three_years",
  "four_years",
  "five_years",
  "six_years",
  "seven_years",
  "eight_years",
  "nine_years",
  "ten_years",
  "eleven_years",
  "twelve_years",
  "thirteen_years",
  "fourteen_years",
  "fifteen_years"
]

const shoulderArthrscopyAndElbowMilestones = [
  "pre_treatment",
  "two_weeks",
  "six_weeks",
  "three_months",
  "six_months",
  "one_year",
  "two_years",
  "five_years",
]

const milestoneToTimelineEventNameMap = {
  "pre_treatment": "Pre-treatment Survey",
  "two_weeks": "2 Weeks Survey",
  "six_weeks": "6 Weeks Survey",
  "three_months": "3 Months Survey",
  "six_months": "6 Months Survey",
  "one_year": "1 Year Survey",
  "two_years": "2 Years Survey",
  "three_years": "3 Years Survey",
  "four_years": "4 Years Survey",
  "five_years": "5 Years Survey",
  "six_years": "6 Years Survey",
  "seven_years": "7 Years Survey",
  "eight_years": "8 Years Survey",
  "nine_years": "9 Years Survey",
  "ten_years": "10 Years Survey",
  "eleven_years": "11 Years Survey",
  "twelve_years": "12 Years Survey",
  "thirteen_years": "13 Years Survey",
  "fourteen_years": "14 Years Survey",
  "fifteen_years": "15 Years Survey"
}

const milestoneToUrlStageMap = {
  "pre_treatment": "pre-treatment",
  "two_weeks": "2-weeks",
  "six_weeks": "6-weeks",
  "three_months": "3-months",
  "six_months": "6-months",
  "one_year": "1-year",
  "two_years": "2-years",
  "three_years": "3-years",
  "four_years": "4-years",
  "five_years": "5-years",
  "six_years": "6-years",
  "seven_years": "7-years",
  "eight_years": "8-years",
  "nine_years": "9-years",
  "ten_years": "10-years",
  "eleven_years": "11-years",
  "twelve_years": "12-years",
  "thirteen_years": "13-years",
  "fourteen_years": "14-years",
  "fifteen_years": "15-years"
}

const PatientDashboard = ({ user, logUserOut }) => {
  const navigate = useNavigate()
  const { customPatientId } = useParams()
  const [showEmail, setShowEmail] = useState(false)
  const [loading, setLoading] = useState(true)
  const [patient, setPatient] = useState({})
  const [vasData, setVasData] = useState([])
  const [saneData, setSaneData] = useState([])
  const [asesFunctionData, setAsesFunctionData] = useState([])
  const [asesIndexData, setAsesIndexData] = useState([])
  const [vr12PhysicalData, setVr12PhysicalData] = useState([])
  const [vr12MentalData, setVr12MentalIndexData] = useState([])
  const [patientEvents, setPatientEvents] = useState([])
  const [timelineEvents, setTimelineEvents] = useState([])
  const [activeMajorEvents, setActiveMajorEvents] = useState([])
  const [selectedActiveMajorEventId, setSelectedActiveMajorEventId] = useState("")
  const [selectedTest, setSelectedTest] = useState("vas")
  const [outliers, setOutliers] = useState([])
  const [outliersLoading, setOutliersLoading] = useState(true)

  useEffect(() => {
    const getPatientByCustomId = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
        const response = await axios.get(`${BASE_URL}/api/v1/patient/${customPatientId}`, config)
        const patient = response.data.patient
        // Check if patient exists
        if (!patient) {
          // Redirect to the not found page if the patient does not exist
          navigate('/404')
        } else {
          setPatient(patient); // Set patient state if found
          setLoading(false);
        }
        setPatient(patient)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getPatientByCustomId()
  }, [customPatientId])

  useEffect(() => {
    const getPatientOutliers = async () => {
      try {
        setOutliersLoading(true)
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
        const response = await axios.get(`${BASE_URL}/api/v1/patient/${customPatientId}/outliers`, config)
        const outliers = response.data.outliers
        setOutliers(outliers)
        setOutliersLoading(false)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
        setOutliersLoading(false)
      }
    }
    getPatientOutliers()
  }, [customPatientId])


  useEffect(() => {
    const getPatientEventsByCustomId = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
        const response = await axios.get(`${BASE_URL}/api/v1/patient/${customPatientId}/events`, config)
        const events = response.data.events
        setPatientEvents(events)
        setActiveMajorEvents(_.sortBy(events.filter(event => event.event_type === "surgery"), "event_date").reverse())
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getPatientEventsByCustomId()
  }, [customPatientId])

  useEffect(() => {
    if(activeMajorEvents.length > 0) {
      setSelectedActiveMajorEventId(activeMajorEvents[0].id)
    }
  }, [activeMajorEvents])

  
  useEffect(() => {
    const getGraphData = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
        const response = await axios.get(`${BASE_URL}/api/v1/patient/${customPatientId}/event/${selectedActiveMajorEventId}/graph-data`, config)
        const data = response.data
        setVasData(data.vasData)
        setSaneData(data.saneData)
        setAsesFunctionData(data.asesFunctionData)
        setAsesIndexData(data.asesIndexData)
        setVr12PhysicalData(data.vr12PhysicalData)
        setVr12MentalIndexData(data.vr12MentalData)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    if(selectedActiveMajorEventId !== "") {
      getGraphData()
    }
  }, [selectedActiveMajorEventId])

  
  useEffect(() => {
    const mapPatientEventsToTimelineEvents = () => {
      // Create surgery color mapping - sort by date to ensure consistent color assignment
      const surgeries = _.sortBy(patientEvents.filter(e => e.event_type === "surgery"), "event_date");
      const hasMultipleSurgeries = surgeries.length > 1;
      const surgeryColorMap = {};
      if (hasMultipleSurgeries) {
        surgeries.forEach((surgery, index) => {
          surgeryColorMap[surgery.id] = SURGERY_COLORS[index % SURGERY_COLORS.length];
        });
      }

      const mappedTimelineEvents = []
      for(const event of patientEvents) {
        switch(event.event_type) {
          case "surgery":
            mappedTimelineEvents.push({
              date: moment(event.event_date).add(12, 'hours'),
              id: event.id,
              name: "Surgery",
              type: "surgery",
              study: event.study === "elbow"? "E" : event.study === "shoulder arthroscopy"? "SAs" : "SAp",
              side: event.treatment_side === "Left"? "L" : "R",
              active: event.event_status === "Active",
              studyFull: event.study,
              sideFull: event.treatment_side,
              surgeryId: event.id,
              surgeryColor: hasMultipleSurgeries ? surgeryColorMap[event.id] : undefined,
            })
            if(["elbow", "shoulder arthroscopy"].includes(event.study)) {
              for(const milestone of shoulderArthrscopyAndElbowMilestones) {
                if(event[`${milestone}_submitted`]) {
                  mappedTimelineEvents.push({
                    date: moment(event[`${milestone}_date_of_submission`]),
                    id: event.id,
                    name: milestoneToTimelineEventNameMap[milestone] + `${event.terms_and_conditions_accepted === false? " (×)" : ""}`,
                    type: "survey",
                    study: event.study === "elbow"? "E" : event.study === "shoulder arthroscopy"? "SAs" : "SAp",
                    side: event.treatment_side === "Left"? "L" : "R",
                    active: event.event_status === "Active",
                    studyFull: event.study,
                    sideFull: event.treatment_side,
                    stage: milestoneToUrlStageMap[milestone],
                    surgeryId: event.id,
                    surgeryColor: hasMultipleSurgeries ? surgeryColorMap[event.id] : undefined,
                    timelineId: `timeline-event-${event.id}-${milestoneToUrlStageMap[milestone]}`,
                  })
                }
                else {
                  if(event[`${milestone}_email_sent`] || event[`${milestone}_first_reminder_sent`]) {
                    const color = hasMultipleSurgeries ? surgeryColorMap[event.id] : undefined;
                    mappedTimelineEvents.push({
                      date: moment(event[`${milestone}_email_sent_timestamp`]),
                      id: event.id,
                      name: `${milestoneToTimelineEventNameMap[milestone]}`,
                      type: "pending-survey",
                      study: event.study === "elbow"? "E" : event.study === "shoulder arthroscopy"? "SAs" : "SAp",
                      side: event.treatment_side === "Left"? "L" : "R",
                      active: event.event_status === "Active",
                      studyFull: event.study,
                      sideFull: event.treatment_side,
                      stage: milestoneToUrlStageMap[milestone],
                      surgeryId: event.id,
                      surgeryColor: color,
                    })
                  }
                }
              }
            }
            else {
              for(const milestone of shoulderArthroplastyMilestones) {
                if(event[`${milestone}_submitted`]) {
                  mappedTimelineEvents.push({
                    date: moment(event[`${milestone}_date_of_submission`]),
                    id: event.id,
                    name: milestoneToTimelineEventNameMap[milestone] + `${event.terms_and_conditions_accepted === false? " (×)" : ""}`,
                    type: "survey",
                    study: event.study === "elbow"? "E" : event.study === "shoulder arthroscopy"? "SAs" : "SAp",
                    side: event.treatment_side === "Left"? "L" : "R",
                    active: event.event_status === "Active",
                    studyFull: event.study,
                    sideFull: event.treatment_side,
                    stage: milestoneToUrlStageMap[milestone],
                    surgeryId: event.id,
                    surgeryColor: hasMultipleSurgeries ? surgeryColorMap[event.id] : undefined,
                    timelineId: `timeline-event-${event.id}-${milestoneToUrlStageMap[milestone]}`,
                  })
                }
                else {
                  if(event[`${milestone}_email_sent`]) {
                    mappedTimelineEvents.push({
                      date: moment(event[`${milestone}_email_sent_timestamp`]),
                      id: event.id,
                      name: milestoneToTimelineEventNameMap[milestone],
                      type: "pending-survey",
                      study: event.study === "elbow"? "E" : event.study === "shoulder arthroscopy"? "SAs" : "SAp",
                      side: event.treatment_side === "Left"? "L" : "R",
                      active: event.event_status === "Active",
                      studyFull: event.study,
                      sideFull: event.treatment_side,
                      stage: milestoneToUrlStageMap[milestone],
                      surgeryId: event.id,
                      surgeryColor: surgeryColorMap[event.id],
                    })
                  }
                }
              }
            }
          break;
          case "clinic visit":
            mappedTimelineEvents.push({
              date: moment(event.event_date),
              id: event.id,
              name: "Clinic Visit",
              type: "clinic-visit",
              study: event.study === "elbow"? "E" : event.study === "shoulder arthroscopy"? "SAs" : "SAp",
              side: patientEvents.filter(patientEvent => patientEvent.id === event.related_event_id)[0].treatment_side === "Left"? "L" : "R",
              studyFull: event.study,
              sideFull: event.treatment_side,
              surgeryId: event.related_event_id,
              surgeryColor: hasMultipleSurgeries ? surgeryColorMap[event.related_event_id] : undefined,
            })
          break;
          case "complication":
            mappedTimelineEvents.push({
              date: moment(event.event_date),
              id: event.id,
              name: "Complication",
              type: "complication",
              study: event.study === "elbow"? "E" : event.study === "shoulder arthroscopy"? "SAs" : "SAp",
              side: patientEvents.filter(patientEvent => patientEvent.id === event.related_event_id)[0].treatment_side === "Left"? "L" : "R",
              studyFull: event.study,
              sideFull: event.treatment_side,
              surgeryId: event.related_event_id,
              surgeryColor: hasMultipleSurgeries ? surgeryColorMap[event.related_event_id] : undefined,
            })
          break;
          default:
            console.log("Unknown event type")
        } 
      }
      setTimelineEvents(_.sortBy(mappedTimelineEvents, "date").reverse())
    }
    mapPatientEventsToTimelineEvents()
  }, [patientEvents])

  const onEventClickHandler = (eventType, eventId, study, side, stage) => {
    switch(eventType) {
      case "surgery":
        // Data nurses go to edit screen, surgeons/admins go to details screen
        if (user.role === 'data_nurse') {
          navigate(`/patients/${customPatientId}/events/surgery/${eventId}/edit`)
        } else {
          navigate(`/patients/${customPatientId}/events/surgery/${eventId}/`)
        }
      break;
      case "clinic-visit": 
        navigate(`/patients/${customPatientId}/events/clinic-visit/${eventId}/edit`)
      break;
      case "survey": 
        navigate(`/patient/${customPatientId}/event/${eventId}/${study.replace(" ", "-")}/${side.toLowerCase()}/survey/${stage}/edit`)
      break;
      case "complication" : 
        navigate(`/patients/${customPatientId}/events/complication/${eventId}/edit`)
      break;
      case "pending-survey" :
        navigate(`/patient/${customPatientId}/event/${eventId}/${study.replace(" ", "-")}/${side.toLowerCase()}/survey/${stage}/new`)
      break;
      default:
        console.log("Unknown event type")
    }
  }

  return (
    <div className="patient-dashboard">
      {loading? (
        <Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
      ) : (
        <>
          <div className='patient-dashboard-header'>
            <div className='patient-id'>
              <h2><BsFillPersonFill /> {customPatientId}</h2>
              <Button onClick={() => navigate(`/patients/${customPatientId}/edit`)}>View/Edit</Button>
            </div>
            <div className='patient-dashboard-subheader'>
              <div className='patient-info'>
                <p><GoMail /> {showEmail && patient.email } (<span className='show-hide-anchor' onClick={() => setShowEmail(!showEmail)}>{showEmail? "hide" : "show"} email</span>)</p> 
                {patient.gender === "M"? <p><FaMale />Male</p> : <p><FaFemale />Female</p>}
                <p><FaRegCalendar /> {moment(patient.date_of_birth).format("DD/MM/YYYY")}</p>
              </div>
            </div>
          </div>
      
          
          <div className='patient-dashboard-body'>
            <div className='left-column'>
              <div className='recent-activity'>
                <div className='recent-activity-header'>
                  <h4><GoClock /> Recent Activity</h4>
                  <Dropdown>
                    <Dropdown.Toggle variant="primary" id="dropdown-basic">
                      <GoPlus /> Add Event
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => navigate(`/patients/${customPatientId}/events/new/surgery`)}>
                        <FaUserMd /> Surgery
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => navigate(`/patients/${customPatientId}/events/new/clinic-visit`)}>
                        <FaStethoscope /> Clinic visit
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => navigate(`/patients/${customPatientId}/events/new/complication`)}>
                        <FaExclamationTriangle /> Complication
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className='timeline'>
                  <CustomTimeline
                    events={timelineEvents}
                    onEventClick={onEventClickHandler}
                  />
                </div>
              </div>
              <div className='alerts'>
                <h4><GoAlert /> Alerts</h4>
                {outliersLoading ? (
                  <div className="alerts-loading">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : outliers.length === 0 ? (
                  <div className="alerts-empty">
                    <p>No alerts - all scores within normal range</p>
                  </div>
                ) : (
                  <div className="alerts-list">
                    {outliers.map((outlier) => (
                      <div
                        key={outlier.vasId}
                        className={`alert-item ${outlier.outlierHandled ? 'handled' : ''}`}
                        onClick={() => {
                          // Convert stage format from "2 weeks" to "2-weeks", "3 months" to "3-months", etc.
                          const urlStage = outlier.stage.replace(/ /g, '-');
                          const timelineId = `timeline-event-${outlier.eventId}-${urlStage}`;
                          const timelineElement = document.getElementById(timelineId);
                          if (timelineElement) {
                            timelineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            // Add a brief highlight effect
                            timelineElement.classList.add('highlighted');
                            setTimeout(() => {
                              timelineElement.classList.remove('highlighted');
                            }, 2000);
                          }
                        }}
                      >
                        <div className="alert-icon">
                          <GoAlert />
                        </div>
                        <div className="alert-content">
                          <div className="alert-score">
                            VAS Score: {outlier.vasScore}
                            {outlier.outlierHandled && <span className="handled-badge">Handled</span>}
                          </div>
                          <div className="alert-milestone">{outlier.stage}</div>
                          <div className="alert-date">
                            {moment(outlier.alertDate).format('MMM DD, YYYY')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className='right-column'>
              <div className='pain-scale'>
                <div className='pain-scale-header'>
                  <h4><GoGraph /> Pain Scale</h4>
                  <div className="active-major-event-filter">
                    <Form.Group className="form-group">
                      <Form.Select
                        value={selectedActiveMajorEventId}
                        onChange={(event) => setSelectedActiveMajorEventId(event.target.value)}>
                        {
                          activeMajorEvents.map((activeMajorEvent, index) => {
                            return (
                              // <option key={index} value={activeMajorEvent.id}>{activeMajorEvent.study.charAt(0).toUpperCase() + activeMajorEvent.study.slice(1)} ({activeMajorEvent.treatment_side})</option>
                              <option key={index} value={activeMajorEvent.id}>{STUDY_ABBREVIATION_MAP[activeMajorEvent.study]} ({activeMajorEvent.treatment_side.charAt(0)}) - {moment(activeMajorEvent.event_date).format("DD/MM/YYYY")}</option>
                            )
                          })
                        }
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
                <div className='graphs'>
                {vasData.length > 0 &&
                  <GraphCard
                    title="VAS Score"
                    legendItems={[
                      { color: 'rgba(173, 181, 189, 0.6)', label: 'Limit' },
                      { color: 'rgb(101, 201, 122)', label: 'Average' },
                      { color: 'rgb(177, 68, 52)', label: 'Patient Score' }
                    ]}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={vasData}
                        margin={{
                          top: 10,
                          right: 5,
                          left: -20,
                          bottom: 50,
                        }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#cbd5e0" strokeWidth={1} />
                        <XAxis
                          dataKey="stage"
                          angle={45}
                          textAnchor="start"
                          interval="preserveStartEnd"
                          dy={10}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <YAxis
                          domain={[0, 10]}
                          tickCount={11}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="limit"
                          isAnimationActive={true}
                          stroke="none"
                          fill="rgba(173, 181, 189, 0.6)"
                          name="Limit"
                        />
                        <Line
                          type="monotone"
                          dataKey="avg"
                          isAnimationActive={true}
                          connectNulls
                          stroke="rgb(101, 201, 122)"
                          strokeWidth={3}
                          dot={{ r: 5, strokeWidth: 3, fill: 'rgb(101, 201, 122)', stroke: '#fff' }}
                          name="Average"
                        />
                        <Line
                          type="monotone"
                          dataKey="score"
                          isAnimationActive={true}
                          connectNulls
                          stroke="rgb(177, 68, 52)"
                          strokeWidth={3}
                          activeDot={{ r: 7, strokeWidth: 4, fill: 'rgb(177, 68, 52)', stroke: '#fff' }}
                          dot={{ r: 5, strokeWidth: 3, fill: 'rgb(177, 68, 52)', stroke: '#fff' }}
                          name="Patient Score"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </GraphCard>
                }
                {saneData.length > 0 &&
                  <GraphCard
                    title="SANE Score"
                    legendItems={[
                      { color: 'rgb(101, 201, 122)', label: 'Average' },
                      { color: 'rgb(177, 68, 52)', label: 'Patient Score' }
                    ]}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={saneData}
                        margin={{
                          top: 10,
                          right: 5,
                          left: -20,
                          bottom: 50,
                        }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#cbd5e0" strokeWidth={1} />
                        <XAxis
                          dataKey="stage"
                          angle={45}
                          textAnchor="start"
                          interval="preserveStartEnd"
                          dy={10}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tickCount={11}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line
                          type="monotone"
                          dataKey="avg"
                          isAnimationActive={true}
                          connectNulls
                          stroke="rgb(101, 201, 122)"
                          strokeWidth={3}
                          dot={{ r: 5, strokeWidth: 3, fill: 'rgb(101, 201, 122)', stroke: '#fff' }}
                          name="Average"
                        />
                        <Line
                          type="monotone"
                          dataKey="score"
                          isAnimationActive={true}
                          connectNulls
                          stroke="rgb(177, 68, 52)"
                          strokeWidth={3}
                          activeDot={{ r: 7, strokeWidth: 4, fill: 'rgb(177, 68, 52)', stroke: '#fff' }}
                          dot={{ r: 5, strokeWidth: 3, fill: 'rgb(177, 68, 52)', stroke: '#fff' }}
                          name="Patient Score"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </GraphCard>
                }
                {asesFunctionData.length > 0 &&
                  <GraphCard
                    title={asesIndexData.length > 0 ? "ASES Function Score" : "ASES Elbow Score"}
                    legendItems={[
                      { color: 'rgb(101, 201, 122)', label: 'Average' },
                      { color: 'rgb(177, 68, 52)', label: 'Function Score' }
                    ]}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={asesFunctionData}
                        margin={{ top: 10, right: 5, left: -30, bottom: 50 }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#cbd5e0" strokeWidth={1} />
                        <XAxis dataKey="stage" angle={45} textAnchor="start" interval="preserveStartEnd" dy={10} tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }} />
                        <YAxis
                          domain={asesIndexData.length > 0 ? [0, 30] : [0, 36]}
                          tickCount={7}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="avg" isAnimationActive={true} connectNulls stroke="rgb(101, 201, 122)" strokeWidth={3} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(101, 201, 122)', stroke: '#fff' }} name="Average" />
                        <Line type="monotone" dataKey="functionScore" isAnimationActive={true} connectNulls stroke="rgb(177, 68, 52)" strokeWidth={3} activeDot={{ r: 7, strokeWidth: 4, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} name="Function Score" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </GraphCard>
                }
                {asesIndexData.length > 0 &&
                  <GraphCard
                    title="ASES Index Score"
                    legendItems={[
                      { color: 'rgb(101, 201, 122)', label: 'Average' },
                      { color: 'rgb(177, 68, 52)', label: 'Index Score' }
                    ]}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={asesIndexData}
                        margin={{ top: 10, right: 5, left: -30, bottom: 50 }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#cbd5e0" strokeWidth={1} />
                        <XAxis dataKey="stage" angle={45} textAnchor="start" interval="preserveStartEnd" dy={10} tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }} />
                        <YAxis
                          domain={[0, 100]}
                          tickCount={11}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="avg" isAnimationActive={true} connectNulls stroke="rgb(101, 201, 122)" strokeWidth={3} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(101, 201, 122)', stroke: '#fff' }} name="Average" />
                        <Line type="monotone" dataKey="indexScore" isAnimationActive={true} connectNulls stroke="rgb(177, 68, 52)" strokeWidth={3} activeDot={{ r: 7, strokeWidth: 4, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} name="Index Score" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </GraphCard>
                }
                {vr12PhysicalData.length > 0 &&
                  <GraphCard
                    title="VR-12 Physical Score"
                    legendItems={[
                      { color: 'rgb(101, 201, 122)', label: 'Average' },
                      { color: 'rgb(177, 68, 52)', label: 'Physical Score' }
                    ]}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={vr12PhysicalData}
                        margin={{ top: 10, right: 5, left: -30, bottom: 50 }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#cbd5e0" strokeWidth={1} />
                        <XAxis dataKey="stage" angle={45} textAnchor="start" interval="preserveStartEnd" dy={10} tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }} />
                        <YAxis
                          domain={[0, 70]}
                          tickCount={8}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="avg" isAnimationActive={true} connectNulls stroke="rgb(101, 201, 122)" strokeWidth={3} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(101, 201, 122)', stroke: '#fff' }} name="Average" />
                        <Line type="monotone" dataKey="physicalScore" isAnimationActive={true} connectNulls stroke="rgb(177, 68, 52)" strokeWidth={3} activeDot={{ r: 7, strokeWidth: 4, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} name="Physical Score" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </GraphCard>
                }
                {vr12MentalData.length > 0 &&
                  <GraphCard
                    title="VR-12 Mental Score"
                    legendItems={[
                      { color: 'rgb(101, 201, 122)', label: 'Average' },
                      { color: 'rgb(177, 68, 52)', label: 'Mental Score' }
                    ]}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={vr12MentalData}
                        margin={{ top: 10, right: 5, left: -30, bottom: 50 }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#cbd5e0" strokeWidth={1} />
                        <XAxis dataKey="stage" angle={45} textAnchor="start" interval="preserveStartEnd" dy={10} tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }} />
                        <YAxis
                          domain={[0, 80]}
                          tickCount={9}
                          tick={{ fill: '#4a5568', fontSize: 12, fontWeight: 600 }} axisLine={{ stroke: '#718096', strokeWidth: 2 }} tickLine={{ stroke: '#718096', strokeWidth: 2 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="avg" isAnimationActive={true} connectNulls stroke="rgb(101, 201, 122)" strokeWidth={3} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(101, 201, 122)', stroke: '#fff' }} name="Average" />
                        <Line type="monotone" dataKey="mentalScore" isAnimationActive={true} connectNulls stroke="rgb(177, 68, 52)" strokeWidth={3} activeDot={{ r: 7, strokeWidth: 4, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} dot={{ r: 5, strokeWidth: 3, fill: 'rgb(177, 68, 52)', stroke: '#fff' }} name="Mental Score" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </GraphCard>
                }
                </div>
              </div>
              
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default PatientDashboard